"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function AIMatchmakingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  const compatibilityFactors = [
    { id: 'factor_values', label: 'Core Values', percentage: 92 },
    { id: 'factor_lifestyle', label: 'Lifestyle', percentage: 88 },
    { id: 'factor_communication', label: 'Communication Style', percentage: 85 },
    { id: 'factor_goals', label: 'Relationship Goals', percentage: 95 },
    { id: 'factor_interests', label: 'Shared Interests', percentage: 78 },
    { id: 'factor_emotional', label: 'Emotional Intelligence', percentage: 90 },
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs uppercase tracking-widest font-semibold mb-6">
              <Icon name="SparklesIcon" variant="solid" size={16} />
              Core Technology
            </div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
              AI That Actually <span className="text-primary">Understands You</span>
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              Our AI analyzes 50+ compatibility factors beyond superficial traits. We look at values, lifestyle, communication style, and relationship goals to find matches you&apos;ll genuinely connect with.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <Icon name="CheckCircleIcon" variant="solid" size={24} className="text-success mt-1" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Deep Personality Analysis</h4>
                  <p className="text-muted-foreground text-sm">
                    10-minute assessment covering psychology-backed compatibility dimensions
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Icon name="CheckCircleIcon" variant="solid" size={24} className="text-success mt-1" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">Continuous Learning</h4>
                  <p className="text-muted-foreground text-sm">
                    AI improves matches based on your feedback and interaction patterns
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Icon name="CheckCircleIcon" variant="solid" size={24} className="text-success mt-1" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">No Bias, Just Data</h4>
                  <p className="text-muted-foreground text-sm">
                    Algorithm focuses on compatibility metrics, not superficial filtering
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Visualization */}
          <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="glass-card p-8 rounded-3xl">
              <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-3">
                <Icon name="ChartBarIcon" variant="solid" size={24} className="text-primary" />
                Sample Compatibility Breakdown
              </h3>
              <div className="space-y-5">
                {compatibilityFactors?.map((factor, index) => (
                  <div key={factor?.id}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-foreground">{factor?.label}</span>
                      <span className="text-sm font-bold text-primary">{factor?.percentage}%</span>
                    </div>
                    <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-1000 ease-out"
                        style={{
                          width: isVisible ? `${factor?.percentage}%` : '0%',
                          transitionDelay: `${index * 100 + 400}ms`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-foreground">Overall Match Score</span>
                  <span className="text-3xl font-bold text-primary">89%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}